import React, { useState, useEffect, useRef } from 'react';
import { Gym } from '../models/gym/Gym';
import { getGym } from '../services/firebase/gym';
import { useAuth } from '../contexts/AuthContext';
import '../styles/GymSelector.css';

interface GymSelectorProps {
  selectedGym: Gym;
  onGymChange: (gymId: string) => void;
  onAddGym?: () => void;
  showAddButton?: boolean;
  disabled?: boolean;
}

const GymSelector: React.FC<GymSelectorProps> = ({ 
  selectedGym, 
  onGymChange, 
  onAddGym,
  showAddButton = false,
  disabled = false 
}) => {
  const [allGyms, setAllGyms] = useState<Gym[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { climberProfile } = useAuth();

  useEffect(() => {
    const fetchUserGyms = async () => {
      if (!climberProfile) {
        setAllGyms([]);
        return;
      }

      setLoading(true);
      try {
        // Get the gym IDs the user is a member of
        const userGymIds = climberProfile.getGymIds();
        
        // Fetch each gym the user is a member of
        const gymPromises = userGymIds.map(gymId => getGym(gymId));
        const gyms = await Promise.all(gymPromises);
        
        // Filter out any null values (in case a gym doesn't exist)
        const validGyms = gyms.filter((gym): gym is Gym => gym !== null);
        
        setAllGyms(validGyms);
      } catch (error) {
        console.error('Error fetching user gyms:', error);
        setAllGyms([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUserGyms();
  }, [climberProfile]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleGymSelect = (gym: Gym) => {
    if (gym.id !== selectedGym.id) {
      onGymChange(gym.id);
    }
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (!disabled && !loading) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className="gym-selector-wrapper">
      <div className="gym-selector" ref={dropdownRef}>
        <button 
          className={`gym-selector-button ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
          onClick={toggleDropdown}
          disabled={disabled || loading}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span className="gym-selector-text">
            {selectedGym.name || 'Select Gym'}
          </span>
          <svg 
            className={`gym-selector-arrow ${isOpen ? 'open' : ''}`}
            width="20" 
            height="20" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path 
              fillRule="evenodd" 
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" 
              clipRule="evenodd" 
            />
          </svg>
        </button>

        {isOpen && (
          <div className="gym-selector-dropdown" role="listbox">
            {loading ? (
              <div className="gym-option-loading">
                <div className="loading-spinner"></div>
                <span>Loading gyms...</span>
              </div>
            ) : allGyms.length === 0 ? (
              <div className="gym-option-empty">
                <span>No gyms joined yet</span>
              </div>
            ) : (
              allGyms.map((gym) => (
                <button
                  key={gym.id}
                  className={`gym-option ${gym.id === selectedGym.id ? 'selected' : ''}`}
                  onClick={() => handleGymSelect(gym)}
                  role="option"
                  aria-selected={gym.id === selectedGym.id}
                >
                  <span className="gym-option-text">{gym.name}</span>
                  {gym.id === selectedGym.id && (
                    <svg 
                      className="gym-option-check"
                      width="16" 
                      height="16" 
                      viewBox="0 0 20 20" 
                      fill="currentColor"
                    >
                      <path 
                        fillRule="evenodd" 
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                        clipRule="evenodd" 
                      />
                    </svg>
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {showAddButton && onAddGym && (
        <button 
          className="gym-add-button"
          onClick={onAddGym}
          disabled={disabled}
          title="Add new gym"
          aria-label="Add new gym"
        >
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path 
              fillRule="evenodd" 
              d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" 
              clipRule="evenodd" 
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default GymSelector;
